import { makeStyles } from "@fluentui/react-components";
import { useMemo } from "react";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { Metric, MetricPanel } from "@/components/metric-panel";
import { BarsVisual, ColumnsVisual, denseBarsHeight, niceTicks, StackedBar } from "@/components/visual/charts";
import { ReportVisual } from "@/components/visual/ReportVisual";
import { VisualHeadline } from "@/components/visual/VisualHeadline";
import { useVisualPageStyles } from "@/components/visual/visual-styles";
import { countBars, countTable, DISTRIBUTION_PREVIEW, distributionView, statusSegments, statusTable, weeklyColumns, weeklyTable, type StatusStackKey } from "@/features/tasks/task-charts";
import { summarize, type Count, type GroupKey } from "@/features/tasks/task-query";
import { EmptyState, useTaskContext } from "@/features/tasks/task-ui";
import { formatDate } from "@/lib/date-format";
import { centerFill, filterScroll } from "@/shell/content-frame";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  body: filterScroll,
  empty: centerFill,
});

/** 분포 카드는 미리보기 5줄에 맞춘다. 더 많은 항목이 있어도 옆 카드가 같이 커지지 않는다. */
const chartHeight = denseBarsHeight(DISTRIBUTION_PREVIEW);

function HelpText({ children }: { children: string }) {
  return <p style={{ margin: 0 }}>{children}</p>;
}

/**
 * 작업 화면의 분석 보기: 실행 Task를 리포트와 같은 순서로 읽는다.
 * 핵심 지표 → 상태 분포 → 항목별 분포.
 * 막대·표 행·지표를 누르면 `onDrill`로 조건(쿼리)을 넘기고, 작업 화면이 그 조건이 걸린 목록으로 바꾼다.
 * 필터 바의 조건은 적용하지 않는다(전체 Task 기준).
 */
export function TaskAnalyticsView({ onDrill }: { onDrill: (query: Record<string, string>) => void }) {
  const styles = useStyles();
  const ps = useVisualPageStyles();
  const ctx = useTaskContext();
  const tasks = useProjectStore((s) => s.project.executionTasks);
  const sum = useMemo(() => summarize(tasks, ctx), [tasks, ctx]);
  const go = onDrill;
  const pct = sum.total ? Math.round((sum.done / sum.total) * 100) : 0;
  const recentDone = sum.completedByWeek.reduce((a, w) => a + w.value, 0);
  const weeklyTicks = niceTicks(Math.max(4, ...sum.completedByWeek.map((w) => w.value)), 4);

  if (tasks.length === 0) {
    return (
      <div className={styles.body}>
        <div className={styles.empty}>
          <EmptyState title="분석할 Task가 없습니다" hint="Task를 만들면 상태·버킷·우선순위·담당자 분포와 지연 현황이 여기에 표시됩니다." />
        </div>
      </div>
    );
  }

  const pickStatus = (k: StatusStackKey) => go({ status: k || "unset" });
  const distributions: Array<{ title: string; description: string; help: string; counts: Count[]; onPick: (key: string) => void }> = [
    { title: "버킷", description: "보드 열에 남은 Task", help: "버킷은 작업 보드의 열입니다. 끝난 Task는 빠지고, 열이 없는 Task는 버킷 없음입니다.", counts: sum.byBucket, onPick: (k) => go({ bucket: k }) },
    { title: "우선순위", description: "급한 일이 어디에 있는지", help: "우선순위가 없는 Task는 미정입니다.", counts: sum.byPriority, onPick: (k) => go({ priority: k || "unset" }) },
    { title: "담당자", description: "사람별 Task", help: "담당이 둘 이상이면 사람마다 한 번씩 들어갑니다.", counts: sum.byAssignee, onPick: (k) => go({ assignee: k }) },
    { title: "WBS 단계", description: "연결된 일정 단계", help: "가장 위 일정 단계에 연결된 Task를 셉니다. 연결이 없으면 범위 미정입니다.", counts: sum.byScope, onPick: (k) => (k ? go({ linked: k }) : go({ group: "scope" satisfies GroupKey, undecided: "1" })) },
    { title: "라벨", description: "라벨별 Task", help: "라벨이 여러 개면 라벨마다 한 번씩 들어갑니다.", counts: sum.byLabel.filter((c) => c.value > 0), onPick: (k) => (k ? go({ label: k }) : go({})) },
  ];
  return (
    <div className={styles.body}>
      <div className={ps.wrap}>
        <VisualHeadline
          ariaLabel="Task 진행과 리스크"
          metrics={
            <MetricPanel ariaLabel="핵심 지표">
              <Metric compact label="완료율" value={pct} unit="%" onClick={() => go({ status: "done" })} ariaLabel={`완료율 ${pct}%, 완료 Task 보기`} />
              <Metric compact label="막힘" value={sum.blocked} unit="개" tone={sum.blocked ? "danger" : "muted"} onClick={() => go({ status: "blocked" })} />
              <Metric compact label="마감 초과" value={sum.overdue} unit="개" tone={sum.overdue ? "danger" : "muted"} onClick={() => go({ due: "overdue" })} />
              <Metric compact label="7일 내 마감" value={sum.dueSoon} unit="개" tone={sum.dueSoon ? "warning" : "muted"} onClick={() => go({ due: "week" })} />
            </MetricPanel>
          }
        />

        <ReportVisual
          title="상태 분포"
          description="조각을 누르면 그 상태의 Task로 이동"
          help={<HelpText>완료, 진행 중, 막힘, 시작 전, 미정이 전체에서 차지하는 비율입니다.</HelpText>}
          meta={`전체 ${sum.total}개`}
          height={56}
          focusHeight={96}
          table={statusTable(sum, pickStatus)}
          chart={() => <StackedBar ariaLabel="상태 분포" segments={statusSegments(sum)} onPick={pickStatus} pickHint="Task 보기" />}
        />

        <div className={ps.grid3}>
          {distributions.map((d) => {
            const view = distributionView(d.counts);
            const barsLabel = (rows: typeof view.preview) => `${d.title} 분포: ${rows.map((c) => `${c.label} ${c.value}개`).join(", ")}`;
            return (
              <ReportVisual
                key={d.title}
                compact
                title={d.title}
                description={d.description}
                help={<HelpText>{d.help}</HelpText>}
                meta={d.counts.length > DISTRIBUTION_PREVIEW ? `${d.counts.length}개 중 ${DISTRIBUTION_PREVIEW}개` : `${d.counts.length}개 항목`}
                height={chartHeight}
                focusHeight={Math.max(chartHeight, denseBarsHeight(view.all.length))}
                table={view.all.length ? countTable(d.title, view.all, d.onPick) : undefined}
                chart={
                  view.preview.length
                    ? (h, expanded) => {
                        const rows = expanded ? view.all : view.preview;
                        return <BarsVisual dense data={countBars(rows, d.onPick)} height={h} ariaLabel={barsLabel(rows)} />;
                      }
                    : undefined
                }
              >
                <GhostEmptyState compact title="데이터 없음" showIcon={false} />
              </ReportVisual>
            );
          })}
          <ReportVisual
            compact
            title="주간 완료"
            description="최근 8주에 끝낸 Task"
            help={<HelpText>그 주에 완료로 바꾼 Task 수입니다.</HelpText>}
            meta={`${recentDone}개`}
            height={chartHeight}
            focusHeight={Math.max(220, chartHeight)}
            table={weeklyTable(sum)}
            chart={(h) => <ColumnsVisual data={weeklyColumns(sum)} height={h} yMin={0} yMax={weeklyTicks.at(-1)} yTicks={weeklyTicks} formatY={(v) => String(Math.round(v))} ariaLabel={`주간 완료: ${sum.completedByWeek.map((w) => `${formatDate(w.weekStart, "short")} ${w.value}개`).join(", ")}`} />}
          />
        </div>
      </div>
    </div>
  );
}
